import React from "react";
import {
  AutoCompleteComponent,
  SelectEventArgs,
} from "@syncfusion/ej2-react-dropdowns";
import { Button, Classes } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import GridColumn from "../../../GridColumns/GridColumn";
import GridView from "./types/GridView";
import AppToasterV2 from "../../../../ToastV2/ToastV2";
import {
  Inject,
  ListViewComponent,
  Virtualization,
} from "@syncfusion/ej2-react-lists";
import ActionButtons from "../../../../Buttons/ActionButtons";
import { Footer } from "../../../../..";

export interface GridViewConfigurationComponentProps {
  viewId: string;
  onCancel?: () => void;
  onSubmit?: () => void;
  getGridView: (viewId: string) => GridView;
  updateViewColumnSettings: (
    viewId: string,
    columnsConfiguration: string[]
  ) => Promise<void>;
  baseGridColumnsConfiguration: GridColumn[] | undefined;
}

type SearchDto = Pick<GridColumn, "headerText" | "field">;
type ListColumn = { id: string; text: string };

// System will use the preload views on store to improve performance as this is the design that is visualized to stay.
const GridViewConfigurationComponent: React.FC<
  GridViewConfigurationComponentProps
> = (props) => {
  const searchRef = React.useRef<AutoCompleteComponent | null>(null);

  const gridView = props.getGridView(props.viewId);
  const [ogColumnConfiguration, setOgColumnConfiguration] = React.useState([
    ...gridView.columnConfiguration,
  ]);
  const [localColumnConfiguration, setLocalColumnConfiguration] =
    React.useState<GridColumn[]>([...gridView.columnConfiguration]);
  const [disableForm, setDisableForm] = React.useState(false);

  const addColumn = (field: string) => {
    // Validate
    if (
      !(
        props.baseGridColumnsConfiguration &&
        !localColumnConfiguration.map((x) => x.field).includes(field)
      )
    ) {
      throw `Cannot add ${field} since its already on the list.`;
    }

    // Get configuration
    const col = props.baseGridColumnsConfiguration.find(
      (x) => x.field === field
    );
    if (!col) {
      throw `Cannot add ${field} sinc eits already on the list.`;
    }

    // Add
    localColumnConfiguration.push(col);
    setLocalColumnConfiguration([...localColumnConfiguration]);
  };
  const removeColumn = (field: string) => {
    setLocalColumnConfiguration(
      localColumnConfiguration.filter((x) => x.field !== field)
    );
  };

  const columnTemplate = (data: ListColumn): React.ReactNode => {
    return (
      <div className="view-column">
        <span>{data.text}</span>
        <Button
          title="Remove"
          className="view-column-remove"
          minimal={true}
          icon={IconNames.BAN_CIRCLE}
          disabled={disableForm}
          onClick={() => {
            removeColumn(data.id);
          }}
        />
      </div>
    );
  };

  const onSearchSelect = (args: SelectEventArgs) => {
    if (!!searchRef.current) {
      const data = args.itemData as SearchDto;
      addColumn(data.field);
    }
  };

  const onSearchActionBegin = (args: { data: SearchDto[] }) => {
    args.data = getSearchColumns();
  };

  const showSearchPopup = () => {
    if (!!searchRef.current) {
      searchRef.current.showPopup();
    }
  };

  const clearSearch = () => {
    if (!!searchRef.current) {
      searchRef.current.clear();
    }
  };

  const getSearchColumns = (): SearchDto[] => {
    const colsNotSelected = props.baseGridColumnsConfiguration!.filter(
      (x) =>
        !localColumnConfiguration.map((y) => y.field).includes(x.field) &&
        x.visible !== false
    );

    return colsNotSelected.map((x) => {
      return { headerText: x.headerText, field: x.field };
    });
  };

  const getListColumns = (): ListColumn[] => {
    return localColumnConfiguration.map((x) => {
      return { text: x.headerText, id: x.field };
    });
  };

  const cancelChanges = () => {
    setLocalColumnConfiguration([...ogColumnConfiguration]);

    // Delegate
    if (props.onCancel) {
      props.onCancel();
    }
  };

  const canSave = (): boolean => {
    return localColumnConfiguration.length > 0;
  };

  const saveView = async () => {
    if (!canSave()) {
      return;
    }

    // Disable
    setDisableForm(true);

    // Validate
    if (
      ogColumnConfiguration.map((x) => x.field).join("") ===
      localColumnConfiguration.map((x) => x.field).join("")
    ) {
      if (props.onCancel) {
        props.onCancel();
      }
      return;
    }

    // Save
    await props
      .updateViewColumnSettings(
        props.viewId,
        localColumnConfiguration.map((x) => x.field)
      )
      .then(() => {
        // Default
        setOgColumnConfiguration([...localColumnConfiguration]);

        AppToasterV2.showSuccessAppToaster("View Configuration Saved");

        // Delegate
        if (props.onSubmit) {
          props.onSubmit();
        }
      })
      .catch(() => {
        setDisableForm(false);

        AppToasterV2.showFailedActionToaster(
          "An error occurred executing the current action."
        );
      });
  };

  return (
    <section className="ucl-grid-view-configuration">
      {/* Search */}
      <div className="ucl-grid-view-configuration-search">
        <label>Search for the fields you would like to add to this view.</label>
        <AutoCompleteComponent
          ref={searchRef}
          id="ucl-grid-view-configuration-search-list"
          className={Classes.INPUT}
          fields={{ value: "headerText" }}
          minLength={0}
          autofill={true}
          showClearButton={false}
          highlight={true}
          placeholder="Type to search..."
          actionBegin={onSearchActionBegin}
          select={onSearchSelect}
          focus={showSearchPopup}
          onClick={showSearchPopup}
          blur={clearSearch}
          change={clearSearch}
          dataSource={getSearchColumns()}
          disabled={disableForm}
        />
      </div>
      {/* View Column Settings */}
      <div className="ucl-grid-view-configuration-columns">
        <label>Added Fields</label>
        <ListViewComponent
          id="ucl-grid-view-configuration-columns-list"
          enableVirtualization={true}
          template={columnTemplate}
          dataSource={getListColumns()}
          enable={!disableForm}
        >
          <Inject services={[Virtualization]} />
        </ListViewComponent>
      </div>
      {/* Save Footer */}
      <Footer>
        <ActionButtons
          actions={[
            {
              text: "Cancel",
              onActionClick: cancelChanges,
              disabled: disableForm,
            },
            {
              text: "Save",
              isPrimary: true,
              onActionClick: saveView,
              disabled: disableForm || !canSave(),
              //tooltip: "Add one or more fields to save",
            },
          ]}
        />
      </Footer>
    </section>
  );
};

const GridViewConfiguration = GridViewConfigurationComponent;
export default GridViewConfiguration;
